import { useState, useEffect, useMemo, useCallback } from 'react';
import throttle from 'lodash/throttle';
import useWindowSize from "./useWindowSize";
const verticalPositions = ['top', 'bottom'];
const horizontalPositions = ['left', 'right'];
const findCompatiblePositions = (primary, secondaryArray) => {
  if (verticalPositions.includes(primary)) {
    return secondaryArray.filter(secondary => horizontalPositions.includes(secondary));
  }
  if (horizontalPositions.includes(primary)) {
    return secondaryArray.filter(secondary => verticalPositions.includes(secondary));
  }
  return [];
};
const useDynamicPosition = ({
  elementRef,
  parentRef,
  preferredPrimaryOrder,
  preferredSecondaryOrder,
  // offsetYPx,
  offsetXPx,
  throttleMs = 200
}) => {
  const isClient = typeof window === 'object';
  const {
    height: currentHeight,
    width: currentWidth
  } = useWindowSize();
  const [primaryPosition, setPrimaryPosition] = useState(preferredPrimaryOrder[0]);
  const compatibleSecondaryPositions = useMemo(() => findCompatiblePositions(primaryPosition, preferredSecondaryOrder), [primaryPosition, preferredSecondaryOrder]);
  const [secondaryPosition, setSecondaryPosition] = useState(compatibleSecondaryPositions[0]);
  const handle = useCallback(throttle(() => {
    const parentNode = parentRef?.current;
    const elementNode = elementRef?.current;
    const {
      top: parentTop,
      bottom: parentBottom,
      right: parentRight,
      left: parentLeft
    } = parentNode?.getBoundingClientRect() || {};
    const {
      width: elementWidth,
      height: elementHeight
    } = elementNode?.getBoundingClientRect() || {};
    const availablePositions = {
      top: parentTop >= elementHeight,
      bottom: currentHeight - parentBottom >= elementHeight,
      right: parentRight >= elementWidth,
      left: currentWidth - parentLeft >= elementWidth
    };
    for (let i = 0; i < preferredPrimaryOrder.length; i += 1) {
      const position = preferredPrimaryOrder[i];
      if (availablePositions[position]) {
        setPrimaryPosition(position);
        break;
      }
    }
    for (let i = 0; i < compatibleSecondaryPositions.length; i += 1) {
      const position = compatibleSecondaryPositions[i];
      if (availablePositions[position]) {
        setSecondaryPosition(position);
        break;
      }
    }
  }, throttleMs), [elementRef, offsetXPx, throttleMs]);
  useEffect(() => {
    if (!isClient) {
      return false;
    }
    handle();
    window.addEventListener('scroll', handle);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle);
      window.removeEventListener('resize', handle);
    };
  }, [handle]);
  return {
    primaryPosition,
    secondaryPosition
  };
};
export default useDynamicPosition;